import { Box, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import useSWRMutation from "swr/mutation";

import { useAppDispatch } from "../../../../../hooks/useRedux";
import StarFillSVG from "../../../../../media/images/icons/star-fill";
import {
  setOpenSnackbar,
  setSnackbarDescription,
  setSnackbarErrorStatus,
} from "../../../../../redux/features/snackbarSlice";
import { fetcherPost } from "../../../../../services/fetcher";
import CustomButton from "../../../../components/CustomButton";
import QuestionCard from "../QuestionCard";
import { convertToJalali } from "../../../../../utils/functions";
import LoadingBox from "../../../../components/LoadingBox";
import EmptyListBox from "../../../../components/EmptyListBox";
import {
  setDialogDescription,
  setDialogStatus,
  setDialogTitle,
  setOpenDialog,
} from "../../../../../redux/features/dialogSlice";
import { ISurveyQuestion } from "../../../../../types/survey";

export default function SurveyForm() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [questions, setQuestions] = useState<ISurveyQuestion[]>([]);
  const validateSubmit = questions.every((e) => e.selected);

  const {
    trigger: getNobatSurveyTrigger,
    data: getNobatSurveyData,
    isMutating: isgetNobatSurveyMutating,
  } = useSWRMutation(
    [`/Nobat/GetNobatSurvey`, localStorage.getItem("access_token")],
    fetcherPost,
    {
      onSuccess: (e) => {
        getSurveyTrigger({});
      },
    }
  );

  const { trigger: getSurveyTrigger, isMutating: getSurveyMutating } =
    useSWRMutation(
      [`/Survey/GetNobatSetOrderSurvey`, localStorage.getItem("access_token")],
      fetcherPost,
      {
        onSuccess: (data) => {
          setQuestions(data);
        },
      }
    );

  const { trigger: submitSurveyTrigger, isMutating: submitSurveyMutating } =
    useSWRMutation(
      [`/Nobat/SetNobatSurvey`, localStorage.getItem("access_token")],
      fetcherPost,
      {
        onSuccess: (data) => {
          dispatch(setOpenDialog(true));
          dispatch(setDialogStatus("success"));
          dispatch(setDialogTitle("ثبت نظرسنجی"));
          dispatch(setDialogDescription("نظرسنجی شما با موفقیت ثبت شد!"));

          setTimeout(() => {
            dispatch(setOpenDialog(false));
          }, 2000);

          getSurveyTrigger({});
          getNobatSurveyTrigger({});
        },
      }
    );

  useEffect(() => {
    getNobatSurveyTrigger({});
  }, []);

  const handleAnswer = (qId: string, selected: string, score: number) => {
    setQuestions((prev) => {
      return prev.map((q) => {
        if (q.id === qId) {
          return { ...q, selected, score };
        } else {
          return q;
        }
      });
    });
  };

  const handleSubmitSurvey = () => {
    let input: number = 0;
    const description = questions
      .map((item) => `${item.qstn}:${item.selected}`)
      .join();

    questions.map((item) => {
      input += item.score || 0;
    });

    submitSurveyTrigger({
      description,
      input,
      id: getNobatSurveyData.id,
    });
  };

  return (
    <Box
      sx={{
        padding: "8px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box
        sx={{
          mx: "auto",
          display: "flex",
          gap: "4px",
          alignItems: "center",
          border: "1px solid",
          padding: "10px",
          borderRadius: "10px",
          backgroundColor: theme.palette.bgColor.blueGrey50,
          borderColor: theme.palette.bgColor.blueGrey100,
        }}
      >
        <StarFillSVG color={theme.palette.secondary.main} />
        <Typography>نظر‌سنجی</Typography>
      </Box>

      {isgetNobatSurveyMutating || getSurveyMutating ? (
        <LoadingBox />
      ) : getNobatSurveyData ? (
        <>
          <Box
            sx={{
              mt: "12px",
              bgcolor: "success.light",
              padding: "10px",
              borderRadius: "8px",
              fontSize: "15px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                }}
              >
                شماره بارنامه:
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                }}
              >
                {getNobatSurveyData?.bol?.bolNr}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                }}
              >
                تاریخ بارنامه:
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                }}
              >
                {
                  convertToJalali(getNobatSurveyData?.bol?.bolDate).split(
                    " "
                  )[1]
                }
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                }}
              >
                مبدا:
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                }}
              >
                {getNobatSurveyData?.terminalInfo?.cityName}{" "}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                }}
              >
                مقصد:
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                }}
              >
                {getNobatSurveyData?.orderInfo?.receiverAddress?.cityName}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              my: 1,
              flex: 1,
              overflowY: "auto",
              gap: "8px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {questions.length
              ? questions.map((item, index) => (
                  <QuestionCard
                    handleAnswer={handleAnswer}
                    index={index}
                    {...item}
                  />
                ))
              : "سوالی یافت نشد!"}
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: "8px",
            }}
          >
            <CustomButton
              onClick={handleSubmitSurvey}
              flex={1}
              justifyContent="center"
              title="ثبت نظرسنجی"
              bgColor="secondary.main"
              fSize="16px"
              px="8px"
              py="8px"
              borderRadius="6px"
              icon1={<StarFillSVG />}
              disabled={submitSurveyMutating || !validateSubmit}
              loading={submitSurveyMutating}
            />
          </Box>
        </>
      ) : (
        <EmptyListBox description="درحال حاضر نظرسنجی فعالی ندارید." />
      )}
    </Box>
  );
}
